import { z } from 'zod/v3';
import { TopicSuggestionSchema } from "../topic-generation";

// Add Source Tool
export const AddSourceSchema = z.object({
  content: z.string().describe('the content or resource to add to the knowledge base'),
});

export type AddSource = z.infer<typeof AddSourceSchema>;

// Edit Blocks Tool (replaces InsertBlock, DeleteBlock, ReplaceBlock, etc.)
export const EditBlocksSchema = z.object({
  operations: z.preprocess(
    (val) => {
      // Handle undefined/null cases - return empty array to fail validation gracefully
      if (val === undefined || val === null) {
        console.warn('EditBlocksSchema: operations parameter is missing or null - LLM called tool without required parameter');
        return []; // Return empty array - will fail min length validation
      }

      // If it's already an array, return as-is
      if (Array.isArray(val)) {
        return val;
      }

      // If it's a string, try to parse it as JSON
      if (typeof val === 'string') {
        try {
          const parsed = JSON.parse(val);
          // Make sure the parsed result is an array
          if (Array.isArray(parsed)) {
            return parsed;
          } else {
            console.error('EditBlocksSchema: Parsed JSON is not an array:', typeof parsed);
            return []; // Return empty array to fail validation
          }
        } catch (e) {
          console.error('EditBlocksSchema: Failed to parse JSON string:', e);
          return []; // Return empty array to fail validation
        }
      }

      console.error('EditBlocksSchema: Received unexpected type:', typeof val);
      return []; // Return empty array to fail validation
    },
    z.array(z.object({
      blockId: z.string().describe("The block's unique ID to operate on"),
      newContent: z.any().describe('Array of new blocks to replace the target block with. Empty array = delete, multiple items = split/insert'),
      oldContent: z.any().optional().describe('The original block content (captured for diff display)'),
      operation: z.enum(['replace', 'insert', 'delete', 'split', 'merge']).optional().describe('Operation type for better UI labeling. split means split the specified blockId with newContent array. merge means merge the specified blockId with its predecessor.'),
      options: z.object({}).optional().describe('Optional parameters for the operation, valid options are: position: "after" | "before" if operation is insert - default is after'),
    })).min(1, 'At least one operation is required')
  ).describe('CRITICAL: Must be a proper JavaScript array of objects, NOT a JSON string! Use this format: [{"blockId": "123", "newContent": [...], "operation": "replace"}]. Never stringify this array or wrap it in quotes!'),
});

export type EditBlocks = z.infer<typeof EditBlocksSchema>;

// Move Block Tool
export const MoveBlockSchema = z.object({
  blockId: z.string().describe("The ID of the block to move."),
  to: z.array(z.number()).describe("The path to move the block to (Plate/Slate Path)."),
  options: z.object({}).optional().describe('Optional parameters for the moveNodes operation'),
});

export type MoveBlock = z.infer<typeof MoveBlockSchema>;

// Edit TOC Tool
export const EditTocSchema = z.object({
  action: z.enum(['add', 'remove', 'update_title', 'reorder']).describe('The type of TOC modification.'),
  itemId: z.string().optional().describe('Identifier for the TOC item (e.g., blockId it points to).'),
  newTitle: z.string().optional().describe('New title for the TOC item (if updating).'),
  newOrder: z.array(z.string()).optional().describe('New order of TOC item IDs (if reordering).'),
  blockId: z.string().optional().describe('The block ID to link to if adding a new TOC item.'),
});

export type EditToc = z.infer<typeof EditTocSchema>; 

/**
 * Suggest a topic from the chat, which can add new content
 */
export const SuggestTopicToolSchema = TopicSuggestionSchema.extend({
  content: z.any().describe('The content to suggest a topic for.'),
})

export type SuggestTopic = z.infer<typeof SuggestTopicToolSchema>;